// ════════════════════════════════════════════════════════════════════════════
// What the block's `kind:` directive says — the PILLAR, named for the reader.
//
// RULED 2026-08-20, verbatim: "kind: names the pillar." The directive stops
// carrying the three-value render taxonomy (`AttachedResultKind`) and carries
// the pillar the attached search came from, so a patents block says patents.
// The directive KEY stays `kind`: it is the word every stored block and every
// model that has re-read one already meets, and the ruling moved the value.
//
// ─── THE PRINTED VALUE IS THE READABLE NAME, NOT THE TWO-LETTER CODE ────────
//
// Three parties read this line — a person reading the version body, the FE
// picking a card family, and a model re-reading content it wrote in an earlier
// round — and only one of them could ever learn that `PT` means patents. The
// two-letter code is OUR minting alphabet for refs, which `follow-up-refs.ts`
// says of itself: the grammar "exists for OUR minting and OUR parsing, not as a
// syntax models are taught to build". A persisted block outlives the ephemeral
// store those refs address (U1), so printing the code into one would export an
// internal alphabet into content that has nothing left to decode it against.
// D19 reaches the same place from the other side: name a block by what it means
// to the reader, never by our field name.
//
// ─── STABLE AND PARSEABLE EITHER WAY ────────────────────────────────────────
//
// A name is one lowercase token, the pillar's own enum member kebab-cased, so
// there is no second vocabulary to keep in step: `RealEstate` is `real-estate`
// and nothing has to be looked up to know that. It can never be confused with
// prose, never needs quoting, and reads back by exact match. It is authored
// ONCE, here, and the reading side is the inverse index built FROM THIS RECORD
// — the writer and the reader cannot disagree about a pillar's spelling, which
// is the map parser's own lesson (one grammar, one home). `hotels` and
// `flights` are deliberately the tokens the kind axis already printed, so those
// two blocks read back unchanged.
//
// `Record<PillarCode, …>` — exhaustive by TYPE, the same shape the projector
// registry and the kind map use: a pillar entering the ref grammar cannot
// compile until someone states what its blocks are called.
// ════════════════════════════════════════════════════════════════════════════

import { PillarCode } from '../session/orchestration/tool-protocol/follow-up-refs.js';

/** The closed vocabulary of printed pillar names — the values a `kind:` line
 *  can carry, declared the way every other printed vocabulary in this grammar
 *  is (a TS enum, never loose strings). One lowercase token each, the pillar's
 *  own enum member kebab-cased. */
export enum AttachPillarName {
  // Travel & place.
  Hotels = 'hotels',
  Flights = 'flights',
  Places = 'places',
  Concerts = 'concerts',
  Weather = 'weather',
  // Commerce & property.
  Products = 'products',
  Apps = 'apps',
  RealEstate = 'real-estate',
  Jobs = 'jobs',
  Finance = 'finance',
  // Knowledge.
  Academic = 'academic',
  Patents = 'patents',
  Books = 'books',
  ClassicTexts = 'classic-texts',
  HistoricalDocuments = 'historical-documents',
  Scriptures = 'scriptures',
  Knowledge = 'knowledge',
  // Body & training.
  Workouts = 'workouts',
  Nutrition = 'nutrition',
  // Media & social.
  Youtube = 'youtube',
  MediaVisual = 'media-visual',
  Music = 'music',
  Social = 'social',
  // Web & current.
  WebSearch = 'web-search',
  Answer = 'answer',
  News = 'news',
  Trends = 'trends',
  Github = 'github',
}

/** One name per pillar, and the table both directions are read through. */
export const ATTACH_PILLAR_NAMES: Record<PillarCode, AttachPillarName> = {
  // Travel & place.
  [PillarCode.Hotels]: AttachPillarName.Hotels,
  [PillarCode.Flights]: AttachPillarName.Flights,
  [PillarCode.Places]: AttachPillarName.Places,
  [PillarCode.Concerts]: AttachPillarName.Concerts,
  [PillarCode.Weather]: AttachPillarName.Weather,
  // Commerce & property.
  [PillarCode.Products]: AttachPillarName.Products,
  [PillarCode.Apps]: AttachPillarName.Apps,
  [PillarCode.RealEstate]: AttachPillarName.RealEstate,
  [PillarCode.Jobs]: AttachPillarName.Jobs,
  [PillarCode.Finance]: AttachPillarName.Finance,
  // Knowledge.
  [PillarCode.Academic]: AttachPillarName.Academic,
  [PillarCode.Patents]: AttachPillarName.Patents,
  [PillarCode.Books]: AttachPillarName.Books,
  [PillarCode.ClassicTexts]: AttachPillarName.ClassicTexts,
  [PillarCode.HistoricalDocuments]: AttachPillarName.HistoricalDocuments,
  [PillarCode.Scriptures]: AttachPillarName.Scriptures,
  [PillarCode.Knowledge]: AttachPillarName.Knowledge,
  // Body & training.
  [PillarCode.Workouts]: AttachPillarName.Workouts,
  [PillarCode.Nutrition]: AttachPillarName.Nutrition,
  // Media & social.
  [PillarCode.Youtube]: AttachPillarName.Youtube,
  [PillarCode.MediaVisual]: AttachPillarName.MediaVisual,
  [PillarCode.Music]: AttachPillarName.Music,
  [PillarCode.Social]: AttachPillarName.Social,
  // Web & current.
  [PillarCode.WebSearch]: AttachPillarName.WebSearch,
  [PillarCode.Answer]: AttachPillarName.Answer,
  [PillarCode.News]: AttachPillarName.News,
  [PillarCode.Trends]: AttachPillarName.Trends,
  [PillarCode.Github]: AttachPillarName.Github,
};

/** What a block from `pillar` says after `kind:`. */
export function attachPillarName(input: { pillar: PillarCode }): AttachPillarName {
  return ATTACH_PILLAR_NAMES[input.pillar];
}

/** The inverse, built from the record above rather than written out — one
 *  authored spelling per pillar, read back by the same table that printed it. */
const PILLAR_BY_ATTACH_NAME: ReadonlyMap<string, PillarCode> = new Map(
  Object.entries(ATTACH_PILLAR_NAMES).map(([pillar, name]) => [name, pillar as PillarCode]),
);

/**
 * The pillar a stored block names, or null when this build does not know the
 * name.
 *
 * Null is not an error condition and is never a guess: BL and FE deploy
 * independently, a block persists longer than either, and the ruled behaviour
 * for a name nobody here recognizes is the neutral render — the block still
 * reads, its items still show, and no card claims to be the pillar's.
 *
 * Case and surrounding space are forgiven for the same reason the parser
 * forgives any bullet and any indent: a surface can reshape them without
 * changing what the line says. Nothing else is — a name is one token, matched
 * whole.
 */
export function attachPillarForName(input: { name: string }): PillarCode | null {
  return PILLAR_BY_ATTACH_NAME.get(input.name.trim().toLowerCase()) ?? null;
}
